import { useEffect } from 'react';
import { isAxiosError } from 'axios';
import { initializeBreadcrumbs, toSafeUrl } from '../services/errorHandling/breadcrumbs.util';
import { getBrowserInfo, getSystemOsInfo } from '../services/errorHandling/browserInfo.util';
import type { ErrorLogPayload, ErrorLogSource, ErrorSeverity } from '../services/errorHandling/errorHandling.types';
import { parseTopStackFrame } from '../services/errorHandling/stackFrame.util';
import {
  buildBaseErrorLogPayload,
  flushPendingErrorLogs,
  getAttachedErrorLogPayload,
  logError,
} from '../services/errorLogger';
import { isAccessDeniedRedirectError } from './accessErrorNavigation';
import { triggerGlobalError } from './ErrorContext';
import { isLegacyCfmUrl, isOnErrorPage, redirectToErrorPage } from './errorPageNavigation.util';

// Benign noise that isn't an actual bug — commonly fired by PDF/iframe viewers on resize.
const IGNORED_MESSAGE_PATTERNS = [/resizeobserver loop/i];

// PostHog / rrweb are third-party. Their session-replay crashes must never log as
// critical or redirect users to the error page. Browser extensions (ad blockers,
// antivirus "anti-tracking" injections, etc.) show up as chrome-extension:// /
// moz-extension:// frames in the stack — e.g. an extension monkey-patching
// window.fetch and throwing "Failed to fetch" — and are equally not our bug.
const IGNORED_THIRD_PARTY_SOURCE_PATTERNS = [
  /posthog/i,
  /rrweb/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
];

function isIgnorableMessage(message: string): boolean {
  return IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

/** 401s are session expiry, not crashes — never send the user to the error page for them. */
function isUnauthorizedApiError(reason: unknown): boolean {
  if (reason == null || typeof reason !== 'object') {
    return typeof reason === 'string' && /status code 401/i.test(reason);
  }

  const status = (reason as { response?: { status?: number } }).response?.status;
  if (status === 401) {
    return true;
  }

  const message = reason instanceof Error ? reason.message : '';
  return /status code 401/i.test(message);
}

function isIgnorableThirdPartySource(source?: string): boolean {
  if (!source) {
    return false;
  }
  return IGNORED_THIRD_PARTY_SOURCE_PATTERNS.some((pattern) => pattern.test(source));
}


/** True when the error came from PostHog/rrweb/a browser extension (filename and/or stack). */
function isIgnorableThirdPartyError(filename?: string, stack?: string): boolean {
  return isIgnorableThirdPartySource(filename) || isIgnorableThirdPartySource(stack);
}

/**
 * True when every frame in the stack is a bare "<anonymous>:line:col" with no
 * resolvable file — the signature of code a browser extension injects directly
 * into the page (e.g. via chrome.scripting.executeScript) rather than anything
 * this app shipped. The browser sometimes attributes these to the page's own URL
 * (e.g. "ReferenceError: mainWorldSdk is not defined" reported with `source` equal
 * to the current page), which slips past the originatesFromOwnOrigin check in
 * handleGlobalError below — this catches it from the stack shape instead, since a
 * genuine app error always has at least one `static/js/...` frame from our bundle.
 */
function isInjectedScriptStack(stack?: string): boolean {
  if (!stack) {
    return false;
  }
  const frameLines = stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('at '));
  return frameLines.length > 0 && frameLines.every((line) => /^at <anonymous>:\d+:\d+$/.test(line));
}

/**
 * Detects the specific "Promise rejected with a non-Error value (object): {}" signature —
 * a plain object with zero own enumerable keys and (necessarily, since describeRejectionReason
 * only gives a non-Error reason no stack at all) no stack trace to trace it by. Ref code 7
 * production log analysis showed this exact signature as a recurring, high-volume entry with
 * no actionable detail — most plausibly PostHog's own internal network calls (or another
 * third-party library) rejecting with an opaque, content-free object when blocked by a
 * corporate proxy or ad-blocker, rather than a real Error. Unlike isIgnorableThirdPartyError
 * above, this can't be confirmed as third-party from the stack (there isn't one), so it's
 * still logged — just demoted, following the isBenignBackgroundChunkFailure precedent in
 * ErrorBoundary.tsx — instead of bouncing the user to the full error page for something with
 * no message a person could act on. DOM Events are excluded: describeEventLikeValue already
 * extracts real diagnostic detail from those, and an Event can still reflect a genuine issue.
 */
function isContentFreeObjectRejection(reason: unknown): boolean {
  if (reason === null || typeof reason !== 'object' || reason instanceof Error) {
    return false;
  }
  if (typeof Event !== 'undefined' && reason instanceof Event) {
    return false;
  }
  return Object.keys(reason).length === 0;
}

/**
 * A 401 left unhandled by its caller. The axios interceptor already runs the session
 * refresh / session-expired flow for it (and deliberately never logs 401s), so
 * reporting it here only logged a "critical" entry and bounced the user to the error
 * page on top of the login redirect.
 */
function isUnauthorizedAxiosRejection(reason: unknown): boolean {
  return isAxiosError(reason) && reason.response?.status === 401;
}

const BARE_NETWORK_FAILURE_MESSAGES = new Set([
  'Failed to fetch',
  'NetworkError when attempting to fetch resource.',
  'Load failed',
]);

/**
 * `fetch()` rejecting with the browser's generic network TypeError and no stack frame
 * from this app — a dropped connection or a blocked third-party/analytics request, not
 * an app bug. Still logged (as a warning) so a real outage stays visible, but it no
 * longer sends the user to the full error page (which, in production, then crashed on
 * its own — the React #310 entries with `message=Failed to fetch`).
 */
function isBareNetworkFailure(reason: unknown, stack?: string): boolean {
  if (!(reason instanceof TypeError) || !BARE_NETWORK_FAILURE_MESSAGES.has(reason.message)) {
    return false;
  }
  if (!stack || typeof window === 'undefined') {
    return true;
  }
  return !stack.includes(window.location.origin);
}

interface OriginDetails {
  originFile?: string;
  originLine?: number;
  originColumn?: number;
  originFunctionName?: string;
  rejectionReasonType?: string;
  thrownValueType?: string;
}

/** Builds the full flat payload, logs it, mirrors it into ErrorContext for the
 * redirect page's dev display, then redirects — same responsibilities as
 * ErrorBoundary.componentDidCatch, for errors no boundary can structurally catch. */
interface ReportAndRedirectOptions {
  /** Defaults to 'critical'. Pass 'info' (matching the isBenignBackgroundChunkFailure
   * precedent) for a signature that's confirmed noise but not confidently third-party
   * enough to fully suppress — see isContentFreeObjectRejection. */
  severity?: ErrorSeverity;
  /** Skips the full-page redirect while still logging. Used for the same noise case:
   * unlike a genuine app error, a content-free rejection carries no message a person
   * could act on, so sending them to the error page for it is disruptive without being
   * useful — and this one isn't confined to a hidden iframe the way silent-sso-callback
   * is, so it reaches real, visible page loads. */
  skipRedirect?: boolean;
  /** Payload already logged upstream for this same failure (e.g. an API error the
   * axios interceptor logged with its X-Request-ID, then left unhandled). When set,
   * nothing new is logged and the redirect carries that same correlationId. */
  upstreamPayload?: ErrorLogPayload;
}

function reportAndRedirect(
  source: Extract<ErrorLogSource, 'window-error' | 'unhandled-rejection'>,
  message: string,
  stack?: string,
  origin?: OriginDetails,
  options?: ReportAndRedirectOptions
): void {
  if (isIgnorableMessage(message)) {
    return;
  }

  const upstreamPayload = options?.upstreamPayload;
  if (upstreamPayload) {
    triggerGlobalError(new Error(message), stack);
    if (!options?.skipRedirect && !isLegacyCfmUrl() && !isOnErrorPage()) {
      redirectToErrorPage(upstreamPayload.correlationId, upstreamPayload.message);
    }
    return;
  }

  const { browserName, browserVersion, screenResolution } = getBrowserInfo();
  const { systemOS, systemOSVersion } = getSystemOsInfo();

  const payload: ErrorLogPayload = {
    ...buildBaseErrorLogPayload(source, options?.severity ?? 'critical', message, stack),
    ...origin,
    browserName,
    browserVersion,
    screenResolution,
    systemOS,
    systemOSVersion,
    isOnline: typeof navigator === 'undefined' ? undefined : navigator.onLine,
  };

  void logError(payload);
  triggerGlobalError(new Error(message), stack);

  if (!options?.skipRedirect && !isLegacyCfmUrl() && !isOnErrorPage()) {
    redirectToErrorPage(payload.correlationId, payload.message);
  }
}

/** Shape of the handful of DOM properties pulled off an Event's `target` below.
 * Declared as a variable type annotation (see its one use in describeEventLikeValue),
 * not an `as` cast — every field here is optional, so a plain `EventTarget` already
 * satisfies this type structurally with no assertion needed. That matters because an
 * `as` cast here previously got silently stripped by `eslint --fix`'s
 * no-unnecessary-type-assertion autofix, which put the code back to erroring on
 * `target.tagName` etc. not existing on plain `EventTarget`. */
type DescribableEventTarget = EventTarget & {
  tagName?: string;
  id?: string;
  className?: string;
  src?: string;
  href?: string;
};

/** DOM Events serialize to `{}` via JSON.stringify (their real properties live on the
 * prototype chain, not as own-enumerable properties) — that's why a rejection reason
 * or thrown value that's actually an Event shows up in logs as an unhelpful empty
 * object. When the value is an Event, pull out the fields that actually explain what
 * happened: the event type, whether it was user-initiated, and a description of the
 * DOM element (if any) it fired on. */
function describeEventLikeValue(value: unknown): Record<string, unknown> | undefined {
  if (typeof Event === 'undefined' || !(value instanceof Event)) {
    return undefined;
  }
  const target: DescribableEventTarget | null = value.target;
  const targetDescriptor = target
    ? [target.tagName, target.id && `#${target.id}`, typeof target.className === 'string' && target.className && `.${target.className}`]
        .filter(Boolean)
        .join('') || undefined
    : undefined;
  return {
    type: value.type,
    isTrusted: value.isTrusted,
    target: targetDescriptor,
    targetSrc: target?.src ?? target?.href,
  };
}

/** `fetch()` / XHR objects rejected or thrown as-is (instead of an Error) —
 * JSON.stringify turns both into `{}`, so pull out what identifies the request. */
function describeRequestLikeValue(value: unknown): Record<string, unknown> | undefined {
  if (typeof Response !== 'undefined' && value instanceof Response) {
    return {
      url: toSafeUrl(value.url),
      status: value.status,
      statusText: value.statusText,
      type: value.type,
    };
  }
  if (typeof XMLHttpRequest !== 'undefined' && value instanceof XMLHttpRequest) {
    return {
      url: toSafeUrl(value.responseURL),
      status: value.status,
      readyState: value.readyState,
    };
  }
  return undefined;
}

const MAX_LISTED_PROPERTIES = 20;

/** Every property name on the value and its prototype chain (short of
 * Object.prototype) — for a value that serializes to `{}`, the names alone often
 * identify what it is (a library's error class, a DOM object, ...). */
function listPropertyNames(value: object): Array<string> {
  const names = new Set<string>();
  let current: object | null = value;
  while (current && current !== Object.prototype && names.size < MAX_LISTED_PROPERTIES) {
    for (const name of Object.getOwnPropertyNames(current)) {
      if (name !== 'constructor') {
        names.add(name);
      }
    }
    current = Object.getPrototypeOf(current) as object | null;
  }
  return [...names].slice(0, MAX_LISTED_PROPERTIES);
}

/** Shared by describeRejectionReason and describeThrownValue below — turns any
 * non-Error value (a raw string, an object, null, undefined...) into a type tag
 * plus a readable, serialized form. `valueType` uses the value's real constructor
 * name (e.g. "Event", "Array") rather than plain `typeof` (always "object"), so a
 * DOM Event doesn't get lumped in with every other plain object in the logs. */
function serializeNonErrorValue(value: unknown): { valueType: string; serialized: string } {
  if (value === null) {
    return { valueType: 'null', serialized: 'null' };
  }
  let constructorName: string | undefined;
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value) as { constructor?: { name?: unknown } } | null;
    if (typeof proto?.constructor?.name === 'string') {
      constructorName = proto.constructor.name;
    }
  }
  const valueType = constructorName ?? typeof value;

  const eventDetails = describeEventLikeValue(value) ?? describeRequestLikeValue(value);
  if (eventDetails) {
    try {
      return { valueType, serialized: JSON.stringify(eventDetails) };
    } catch {
      // Fall through to the generic path below.
    }
  }

  let serialized: string;
  try {
    serialized = typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    // JSON.stringify can throw (BigInt, circular refs...). Build the fallback by
    // typeof rather than a bare String(value): String() falls back to
    // Object.prototype.toString ("[object Object]") for plain objects/arrays, which
    // @typescript-eslint/no-base-to-string flags — TS can't prove the narrowed type
    // in a combined `typeof value === 'object' && value !== null` ternary excludes
    // that case, so each branch below calls a type's own real toString() (or a
    // literal) instead, which is unambiguous either way.
    switch (typeof value) {
      case 'number':
      case 'boolean':
      case 'bigint':
      case 'symbol': {
        serialized = value.toString();
        break;
      }
      case 'undefined': {
        serialized = 'undefined';
        break;
      }
      case 'function': {
        serialized = '[Function]';
        break;
      }
      default: {
        serialized = value === null ? 'null' : Object.prototype.toString.call(value);
      }
    }
  }
  // `{}` says nothing — name what the object actually has instead.
  if (serialized === '{}' && typeof value === 'object' && value !== null) {
    const propertyNames = listPropertyNames(value);
    serialized =
      propertyNames.length > 0
        ? `{} [properties: ${propertyNames.join(', ')}]`
        : '{} [no properties]';
  }
  return { valueType, serialized };
}

/** `event.error` when it exists but isn't a real Error instance — a raw value was
 * thrown outside React (e.g. `throw "boom"` or `throw { code: 1 }`). */
function describeThrownValue(thrownValue: unknown): { message: string; thrownValueType: string } {
  const { valueType, serialized } = serializeNonErrorValue(thrownValue);
  return {
    message: `Uncaught non-Error value thrown (${valueType}): ${serialized}`,
    thrownValueType: valueType,
  };
}

/** Non-Error rejection reasons (a raw string, an object, undefined...) carry no stack — capture what we can instead. */
function describeRejectionReason(reason: unknown): { message: string; stack?: string; reasonType: string } {
  if (reason instanceof Error) {
    return { message: reason.message, stack: reason.stack, reasonType: 'Error' };
  }
  const { valueType, serialized } = serializeNonErrorValue(reason);
  return {
    message: `Promise rejected with a non-Error value (${valueType}): ${serialized}`,
    reasonType: valueType,
  };
}

/**
 * Handles global JavaScript errors (window.onerror). Catches undefined-variable
 * errors, runtime errors, and any uncaught exception no ErrorBoundary structurally sees.
 */
function handleGlobalError(
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): boolean {
  // Prevent handling errors from external scripts (browser extensions, etc.). A
  // genuine app error always has a `source` pointing at one of our own bundled
  // chunk files. Extension-injected scripts commonly report NO source at all
  // (e.g. "ReferenceError: mainWorldSdk is not defined" at "<anonymous>:1:22")
  // since they have no src URL — an empty source used to fall through this check
  // entirely and got logged as critical *and* redirected the user off the page.
  if (typeof window !== 'undefined') {
    const originatesFromOwnOrigin = (value?: string): boolean =>
      !!value && value.includes(window.location.origin);
    if (!originatesFromOwnOrigin(source) && !originatesFromOwnOrigin(error?.stack)) {
      return false;
    }
  }

  if (isLegacyCfmUrl() || isOnErrorPage()) {
    return false;
  }

  const hasErrorObject = error instanceof Error;
  const stack = hasErrorObject ? error.stack : undefined;
  const messageText = typeof message === 'string' ? message : error?.message ?? '';

  if (
    isIgnorableMessage(messageText) ||
    isIgnorableThirdPartyError(source, stack) ||
    isInjectedScriptStack(stack) ||
    isUnauthorizedApiError(error) ||
    /status code 401/i.test(messageText)
  ) {
    return true;
  }

  try {
    const frame = parseTopStackFrame(stack);
    const hasThrownNonErrorValue = !hasErrorObject && error !== undefined;
    const thrownValueDetails = hasThrownNonErrorValue ? describeThrownValue(error) : undefined;

    const resolvedMessage =
      thrownValueDetails?.message ||
      messageText ||
      (message instanceof Event ? `Error: ${message.type || 'Unknown event error'}` : 'Unknown JavaScript error occurred');

    reportAndRedirect('window-error', resolvedMessage, stack, {
      // ErrorEvent's own filename/lineno/colno are browser-native and more reliable
      // than parsing the stack — the stack is only a fallback when those are absent.
      originFile: source || frame?.fileName,
      originLine: lineno || frame?.line,
      originColumn: colno || frame?.column,
      originFunctionName: frame?.functionName,
      thrownValueType: thrownValueDetails?.thrownValueType,
    }, { upstreamPayload: getAttachedErrorLogPayload(error) });

    return true;
  } catch {
    // If error handling itself fails, let the browser handle it.
    return false;
  }
}

/** Handles unhandled promise rejections (failed API calls without .catch(), async errors, etc.). */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  if (isLegacyCfmUrl() || isOnErrorPage()) {
    return;
  }

  try {
    if (isAccessDeniedRedirectError(event.reason) || isUnauthorizedApiError(event.reason)) {
      event.preventDefault?.();
      return;
    }

    if (isUnauthorizedAxiosRejection(event.reason)) {
      event.preventDefault?.();
      return;
    }

    // Navigation-only sentinels thrown by the axios interceptor ("Redirecting to …")
    // after it already logged the real failure and navigated the user to the right
    // page — re-reporting them would only send the user on to the global error page.
    if (
      event.reason instanceof Error &&
      event.reason.message.startsWith('Redirecting') &&
      getAttachedErrorLogPayload(event.reason)
    ) {
      event.preventDefault?.();
      return;
    }

    const { message, stack, reasonType } = describeRejectionReason(event.reason);
    const frame = parseTopStackFrame(stack);

    if (
      isIgnorableMessage(message) ||
      isIgnorableThirdPartyError(frame?.fileName, stack) ||
      isInjectedScriptStack(stack)
    ) {
      event.preventDefault?.();
      return;
    }

    const isContentFreeObject = isContentFreeObjectRejection(event.reason);
    const isNetworkFailure = isBareNetworkFailure(event.reason, stack);

    reportAndRedirect(
      'unhandled-rejection',
      message,
      stack,
      {
        originFile: frame?.fileName,
        originLine: frame?.line,
        originColumn: frame?.column,
        originFunctionName: frame?.functionName,
        rejectionReasonType: reasonType,
      },
      {
        ...(isContentFreeObject ? { severity: 'info' as const, skipRedirect: true } : {}),
        ...(isNetworkFailure ? { severity: 'warning' as const, skipRedirect: true } : {}),
        upstreamPayload: getAttachedErrorLogPayload(event.reason),
      }
    );

    event.preventDefault?.();
  } catch {
    // If error handling itself fails, let it propagate.
  }
}

/**
 * Hook to initialize global error listeners
 * Should be called once at app startup
 */
export function useGlobalErrorListeners(): void {
  useEffect(() => {
    // Only initialize in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Store existing handlers to preserve them
    const existingOnError = window.onerror;
    // Note: unhandledrejection uses addEventListener, not a property, so we can't preserve it the same way
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _existingOnUnhandledRejection = window.onunhandledrejection;

    // Set up global JavaScript error handler
    // Note: window.onerror is required for global JS error handling (different from addEventListener('error'))
    // eslint-disable-next-line unicorn/prefer-add-event-listener
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ): boolean => {
      // Call existing handler first if it exists
      let handled = false;
      if (existingOnError) {
        handled = existingOnError(message, source, lineno, colno, error) === true;
      }

      // Call our handler
      const ourHandled = handleGlobalError(message, source, lineno, colno, error);

      // Return true if either handler wants to suppress default behavior
      return handled || ourHandled;
    };

    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Records route changes, clicks, failed resource loads and failed requests so
    // every error log carries what happened just before it (breadcrumbs.util.ts).
    initializeBreadcrumbs();

    // Cleanup function
    return () => {
      // Restore original handler
      // eslint-disable-next-line unicorn/prefer-add-event-listener
      window.onerror = existingOnError;
      // Remove our listener
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []); // Empty deps - only run once on mount
}

/**
 * Initialize global error listeners (for use outside React components)
 * Call this once when app starts
 */
export function initializeGlobalErrorListeners(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Store existing handlers
  const existingOnError = window.onerror;
  // Note: unhandledrejection uses addEventListener, not a property, so we can't preserve it the same way
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _existingOnUnhandledRejection = window.onunhandledrejection;

  // Set up window.onerror
  // Note: window.onerror is required for global JS error handling (different from addEventListener('error'))
  // eslint-disable-next-line unicorn/prefer-add-event-listener
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    let handled = false;
    if (existingOnError) {
      handled = existingOnError(message, source, lineno, colno, error) === true;
    }
    const ourHandled = handleGlobalError(message, source, lineno, colno, error);
    return handled || ourHandled;
  };

  // Set up unhandledrejection listener
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // See the matching call in useGlobalErrorListeners — idempotent.
  initializeBreadcrumbs();

  // Retry any error logs that failed to send while offline — once now (in case
  // entries survived a reload from a previous offline session) and again every
  // time the browser regains connectivity.
  void flushPendingErrorLogs();
  window.addEventListener('online', () => {
    void flushPendingErrorLogs();
  });
}
